import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import { Business as BusinessIcon, AdminPanelSettings as RoleIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import TenantService from '../../services/tenant.service';
import RoleService from '../../services/role.service';

const TenantRolesManagement = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenantRoleIds, setTenantRoleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canEdit = hasPermission('tenantroles.create');

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadTenantRoles(selectedTenantId);
    } else {
      setTenantRoleIds([]);
    }
  }, [selectedTenantId]);

  const loadInitial = async () => {
    try {
      setLoading(true);
      const [tenantsRes, rolesRes] = await Promise.all([
        TenantService.getAll(),
        RoleService.getAll(),
      ]);
      const tenantsData = tenantsRes.data?.data || tenantsRes.data || [];
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
      setAllRoles(rolesRes.data || []);
      if (!selectedTenantId && tenantsData.length > 0) {
        setSelectedTenantId(String(tenantsData[0].id));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('tenantRoles.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantRoles = async (tenantId) => {
    try {
      const res = await TenantService.getRoles(tenantId);
      const roles = res.data || [];
      setTenantRoleIds(roles.map((r) => r.id));
    } catch (error) {
      console.error('Error loading tenant roles:', error);
      setTenantRoleIds([]);
      showSnackbar(t('tenantRoles.errorLoadingRoles'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleRole = (roleId) => {
    if (!canEdit) return;
    setTenantRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSave = async () => {
    if (!selectedTenantId || !canEdit) return;
    try {
      setSaving(true);
      await TenantService.syncRoles(selectedTenantId, tenantRoleIds);
      showSnackbar(t('tenantRoles.savedSuccess'));
    } catch (error) {
      console.error('Error saving tenant roles:', error);
      showSnackbar(error.response?.data?.message || t('tenantRoles.errorSaving'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('tenantRoles.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('tenantRoles.description')}
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }} size="medium">
            <InputLabel>{t('tenantRoles.selectTenant')}</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              label={t('tenantRoles.selectTenant')}
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={String(tenant.id)}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTenantId && (
            <>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {t('tenantRoles.rolesForTenant')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('tenantRoles.rolesHint')}
              </Typography>

              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {allRoles.map((role) => (
                  <ListItem
                    key={role.id}
                    secondaryAction={
                      tenantRoleIds.includes(role.id) && (
                        <Chip label={t('tenantRoles.assigned')} size="small" color="primary" variant="outlined" />
                      )
                    }
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={tenantRoleIds.includes(role.id)}
                        disabled={!canEdit}
                        onChange={() => handleToggleRole(role.id)}
                      />
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <RoleIcon color="action" />
                    </ListItemIcon>
                    <ListItemText primary={role.name} secondary={role.description || null} />
                  </ListItem>
                ))}
              </List>

              {canEdit && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ mt: 2 }}
                >
                  {saving ? t('common.saving') : t('common.save')}
                </Button>
              )}
            </>
          )}

          {!selectedTenantId && tenants.length === 0 && (
            <Alert severity="info">{t('tenantRoles.noTenants')}</Alert>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantRolesManagement;
